import { useState, useEffect, useRef } from "react";
import { T, mono, uid } from "./utils";
import { supabase } from "./supabase";

const DAYS_TR = ["Pazartesi","Salı","Çarşamba","Perşembe","Cuma","Cumartesi","Pazar"];
const DAYS_SHORT = ["PZT","SAL","ÇAR","PER","CUM","CMT","PAZ"];
const MONTHS_TR = ["Ocak","Şubat","Mart","Nisan","Mayıs","Haziran","Temmuz","Ağustos","Eylül","Ekim","Kasım","Aralık"];
const GCAL_COLORS = {"1":"#7986cb","2":"#33b679","3":"#8e24aa","4":"#e67c73","5":"#f6c026","6":"#f5511d","7":"#039be5","8":"#616161","9":"#3f51b5","10":"#0b8043","11":"#d60000"};
const CAL_BLACKLIST = ["fol films meeting","türkiye","tatil","holiday","birthday","doğum","contacts"];
const WEEKLY_TARGET_MIN = 900;

const WHITE = "#fff";
const CEVIK_COLOR = "#4a6fa5";
const ADA_COLOR = "#7c6dfa";

function weekdayIndex(d){return (d.getDay()+6)%7;}
function addDays(date,n){const d=new Date(date);d.setDate(d.getDate()+n);return d;}
function getWeekStart(date){const d=new Date(date);d.setDate(d.getDate()-weekdayIndex(d));d.setHours(0,0,0,0);return d;}
function toDateStr(date){return `${date.getFullYear()}-${String(date.getMonth()+1).padStart(2,"0")}-${String(date.getDate()).padStart(2,"0")}`;}
function isAraTask(t){ return t.text.trim().toUpperCase().startsWith("ARA:"); }
function taskSortTier(t){ if(isAraTask(t)) return 2; if(t.personal) return 1; return 0; }

async function fetchGCalWeek(googleToken,weekStart){
  if(!googleToken) return [];
  try{
    const timeMin=new Date(weekStart).toISOString();
    const timeMax=addDays(weekStart,7).toISOString();
    const calRes=await fetch("https://www.googleapis.com/calendar/v3/users/me/calendarList",{headers:{Authorization:`Bearer ${googleToken}`}});
    const calData=await calRes.json();
    if(calData.error) return [];
    const calendars=(calData.items||[]).filter(c=>!CAL_BLACKLIST.some(b=>(c.summary||"").toLowerCase().includes(b)));
    const all=await Promise.all(calendars.map(async cal=>{
      try{
        const r=await fetch(`https://www.googleapis.com/calendar/v3/calendars/${encodeURIComponent(cal.id)}/events?timeMin=${encodeURIComponent(timeMin)}&timeMax=${encodeURIComponent(timeMax)}&singleEvents=true&orderBy=startTime&maxResults=50`,{headers:{Authorization:`Bearer ${googleToken}`}});
        const d=await r.json();
        if(d.error) return [];
        return (d.items||[]).map(e=>({id:e.id,calId:cal.id,title:e.summary||"(başlıksız)",date:e.start?.date||e.start?.dateTime?.slice(0,10),time:e.start?.dateTime?e.start.dateTime.slice(11,16):null,color:e.colorId?GCAL_COLORS[e.colorId]:(cal.backgroundColor||CEVIK_COLOR),calendarName:cal.summary}));
      }catch{return [];}
    }));
    return all.flat();
  }catch{return [];}
}

function EditableTaskText({text, done, onSave, colorOverride}) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(text);
  const taRef = useRef();
  useEffect(()=>setVal(text),[text]);
  useEffect(()=>{
    if(editing && taRef.current){
      taRef.current.style.height="auto";
      taRef.current.style.height=taRef.current.scrollHeight+"px";
      taRef.current.focus();
      taRef.current.setSelectionRange(taRef.current.value.length,taRef.current.value.length);
    }
  },[editing]);
  const commit=()=>{setEditing(false);if(val.trim()&&val.trim()!==text) onSave(val.trim());};
  const autoGrow=(e)=>{ e.target.style.height="auto"; e.target.style.height=e.target.scrollHeight+"px"; };
  if(editing) return (
    <textarea ref={taRef} value={val}
      onChange={e=>{setVal(e.target.value);autoGrow(e);}}
      onBlur={commit}
      onKeyDown={e=>{if(e.key==="Enter"&&!e.shiftKey){e.preventDefault();commit();}if(e.key==="Escape"){setVal(text);setEditing(false);}}}
      rows={1}
      style={{flex:1,fontSize:14,border:"none",borderBottom:`1px solid ${T.accent}`,padding:0,fontFamily:"'Inter',sans-serif",color:T.ink,background:"transparent",outline:"none",width:"100%",minWidth:0,resize:"none",overflow:"hidden",lineHeight:1.4}}/>
  );
  return (
    <span onDoubleClick={()=>setEditing(true)} title="Düzenlemek için çift tıkla"
      style={{fontSize:14,color:colorOverride||(done?T.mute:T.ink),textDecoration:done?"line-through":"none",lineHeight:1.4,cursor:"text",minWidth:0,overflowWrap:"break-word"}}>
      {text}
    </span>
  );
}

function TaskRow({t, colAssignee, accessLevel, selectorProjects, hours, setHour, toggleTask, renameTask, reassignTask, assignTask, postponeTask, removeTask, onToggleBlock}){
  const isAdaCol = colAssignee==="ADA";
  const [hovered,setHovered]=useState(false);
  return (
    <div onMouseEnter={()=>setHovered(true)} onMouseLeave={()=>setHovered(false)}
      style={{display:"flex",alignItems:"center",gap:10,padding:"11px 0",borderBottom:`0.5px solid ${T.borderLight}`}}>
      <div onClick={()=>toggleTask(t.id,t.done)} style={{width:16,height:16,borderRadius:4,flexShrink:0,cursor:"pointer",background:t.done?T.ink:"transparent",border:`1.5px solid ${t.done?T.ink:T.border}`,display:"flex",alignItems:"center",justifyContent:"center"}}>
        {t.done&&<span style={{color:"#fff",fontSize:9}}>✓</span>}
      </div>
      <div style={{flex:1,minWidth:0,display:"flex",alignItems:"center",gap:6,flexWrap:"wrap"}}>
        <EditableTaskText text={t.text} done={t.done} onSave={v=>renameTask(t.id,v)} colorOverride={isAraTask(t)?"#2d7a2d":(t.personal?"#7ba3d0":undefined)}/>
        <select value={t.filmTitle||"ETC."} onChange={e=>reassignTask(t.id,e.target.value)}
          style={{...mono,fontSize:9,color:T.mute,background:"transparent",border:"none",cursor:"pointer",padding:0,outline:"none",maxWidth:120,appearance:"none",WebkitAppearance:"none",MozAppearance:"none"}}>
          <option value="ETC.">ETC.</option>
          {accessLevel==="full"&&<option value="Personal">Personal</option>}
          {selectorProjects.map(p=><option key={p.id} value={p.title}>{p.title}</option>)}
        </select>
        {t.assignee2&&<span style={{...mono,fontSize:9,color:T.mute}}>+{isAdaCol?"CEVİK":"ADA"}</span>}
      </div>
      {t.done && (isAdaCol || accessLevel==="full") && (
        <span style={{fontSize:13,fontWeight:600,color:T.ink,whiteSpace:"nowrap",flexShrink:0,display:"flex",alignItems:"center",gap:3}}>
          <span style={{color:T.mute,fontWeight:400}}>—</span>
          <input value={hours[t.id]||""} onChange={e=>setHour(t.id,e.target.value.replace(/[^0-9]/g,""))} placeholder="0"
            style={{width:24,textAlign:"right",border:"none",background:"transparent",color:T.ink,outline:"none",fontFamily:"inherit",fontSize:13,fontWeight:600,padding:0}}/>
          <span>'</span>
        </span>
      )}
      <div style={{display:"flex",gap:6,flexShrink:0,width:onToggleBlock?76:54,justifyContent:"flex-end",opacity:hovered?1:0,visibility:hovered?"visible":"hidden",transition:"opacity .1s"}}>
        {onToggleBlock&&<span onClick={()=>onToggleBlock(t.id,t.block)} style={{...mono,fontSize:9,color:T.mute,cursor:"pointer"}} title="Diğer bloğa taşı">{t.block===2?"→I":"→II"}</span>}
        <span onClick={()=>assignTask(t.id,isAdaCol?"CEVIK":"ADA")} style={{...mono,fontSize:9,color:T.mute,cursor:"pointer"}} title="Diğerine ata">⇄</span>
        {!t.done&&<span onClick={()=>postponeTask(t.id,t.due_date)} style={{...mono,fontSize:9,color:T.mute,cursor:"pointer"}} title="Yarına ertele">→</span>}
        <span onClick={()=>removeTask(t.id)} style={{fontSize:13,color:T.mute,cursor:"pointer",lineHeight:1}} title="Sil">×</span>
      </div>
    </div>
  );
}

function AddRow({ sectionKey, assignee, addingFor, setAddingFor, newText, setNewText, handleAddTask, newFilm, setNewFilm, newShared, setNewShared, accessLevel, selectorProjects, addInputRef }){
  const isOpen = addingFor===sectionKey;
  if(!isOpen) return (
    <div onClick={()=>setAddingFor(sectionKey)} style={{padding:"11px 0",fontSize:14,color:T.mute,cursor:"pointer"}}>+ ekle</div>
  );
  return (
    <div style={{display:"flex",alignItems:"center",gap:8,padding:"9px 0",flexWrap:"wrap"}}>
      <input ref={addInputRef} value={newText} onChange={e=>setNewText(e.target.value)}
        onKeyDown={e=>{if(e.key==="Enter")handleAddTask();if(e.key==="Escape"){setAddingFor(null);setNewText("");}}}
        onBlur={()=>{ if(!newText.trim()) setAddingFor(null); }}
        placeholder="Görev yaz…"
        style={{flex:1,minWidth:100,border:"none",borderBottom:`1px solid ${T.border}`,fontSize:14,fontFamily:"'Inter',sans-serif",color:T.ink,outline:"none",background:"transparent",padding:"2px 0"}}/>
      <select value={newFilm} onChange={e=>setNewFilm(e.target.value)}
        style={{...mono,fontSize:9,color:T.mute,background:"transparent",border:"none",cursor:"pointer",outline:"none",maxWidth:100}}>
        <option value="ETC.">ETC.</option>
        {accessLevel==="full"&&<option value="Personal">Personal</option>}
        {selectorProjects.map(p=><option key={p.id} value={p.title}>{p.title}</option>)}
      </select>
      <span onClick={()=>setNewShared(s=>!s)} style={{...mono,fontSize:9,color:newShared?T.ink:T.border,cursor:"pointer",whiteSpace:"nowrap"}} title="Her ikisine de ata">
        +{assignee==="ADA"?"CEVİK":"ADA"}
      </span>
    </div>
  );
}

export function HomeView({googleToken, accessLevel}){
  const now=new Date();
  const todayStr=toDateStr(now);
  const todayIdx=weekdayIndex(now);

  const [weekStart,setWeekStart]=useState(getWeekStart(now));
  const [activeDay,setActiveDay]=useState(todayIdx);
  const [gcalEvents,setGcalEvents]=useState([]);
  const [tasks,setTasks]=useState([]);
  const [igPosts,setIgPosts]=useState([]);
  const [projects,setProjects]=useState([]);
  const [loading,setLoading]=useState(true);
  const [addingFor,setAddingFor]=useState(null); // "CEVIK" | "ADA" | null
  const [newText,setNewText]=useState("");
  const [newFilm,setNewFilm]=useState("ETC.");
  const [newShared,setNewShared]=useState(false);
  const [showSearch,setShowSearch]=useState(false);
  const [searchQuery,setSearchQuery]=useState("");
  const [searchResults,setSearchResults]=useState([]);
  const [showCatchup,setShowCatchup]=useState(false);
  const [hiddenProjectIds, setHiddenProjectIds] = useState([]);
  const [hours, setHours] = useState({});
  const [notes, setNotes] = useState({ CEVIK: "", ADA: "", GENEL: "" });
  const noteTimers = useRef({});
  const hourTimers = useRef({});
  const addInputRef = useRef();

  useEffect(()=>{loadAll();},[]); // eslint-disable-line
  useEffect(()=>{
    supabase.from("assignee_notes").select("assignee,note").then(({data})=>{
      const n={CEVIK:"",ADA:"",GENEL:""};
      (data||[]).forEach(r=>{n[r.assignee]=r.note;});
      setNotes(n);
    });
  },[]); // eslint-disable-line
  useEffect(()=>{
    supabase.from("app_settings").select("value").eq("key","hidden_task_projects").maybeSingle().then(({data})=>{
      setHiddenProjectIds(data?.value?.ids||[]);
    });
  },[]); // eslint-disable-line
  useEffect(()=>{
    if(googleToken){fetchGCalWeek(googleToken,weekStart).then(setGcalEvents);}
    loadIgPosts();
    loadHours();
  },[weekStart,googleToken]); // eslint-disable-line
  useEffect(()=>{ if(addingFor&&addInputRef.current) addInputRef.current.focus(); },[addingFor]);

  async function loadAll(){
    setLoading(true);
    const {data:projs}=await supabase.from("projects").select("id,title,kind,phase");
    setProjects(projs||[]);
    await reloadTasks(projs);
    setLoading(false);
  }

  async function reloadTasks(projs){
    const {data:taskRows}=await supabase.from("tasks").select("id,text,done,due_date,project_id,etc,assignee,assignee2,notified,personal,block").not("due_date","is",null).order("due_date");
    const {data:allProjs}=projs?{data:projs}:await supabase.from("projects").select("id,title,kind,phase");
    const projMap={};(allProjs||[]).forEach(p=>projMap[p.id]={title:p.title,kind:p.kind});
    const mapped=(taskRows||[]).map(t=>({...t,filmTitle:t.project_id?(projMap[t.project_id]?.title||"ETC."):(t.personal?"Personal":"ETC."),filmKind:projMap[t.project_id]?.kind||"",assignee:t.assignee||"CEVIK",block:t.block||1}));
    setTasks(accessLevel==="full"?mapped:mapped.filter(t=>!t.personal));
  }

  async function loadHours(){
    const weekKey = toDateStr(weekStart);
    const { data } = await supabase.from("summary_weeks").select("hours").eq("week_start",weekKey).maybeSingle();
    setHours(data?.hours||{});
  }

  function setHour(taskId, value){
    setHours(prev=>({...prev,[taskId]:value}));
    clearTimeout(hourTimers.current[taskId]);
    hourTimers.current[taskId]=setTimeout(async()=>{
      const weekKey = toDateStr(weekStart);
      const { data } = await supabase.from("summary_weeks").select("hours").eq("week_start",weekKey).maybeSingle();
      const nextHours = { ...(data?.hours||{}), [taskId]: value };
      await supabase.from("summary_weeks").upsert({ week_start:weekKey, hours:nextHours }, { onConflict:"week_start" });
    },500);
  }

  async function searchTasks(q) {
    if (!q.trim()) { setSearchResults([]); return; }
    const safe = q.trim().replace(/[%_\\]/g, m => "\\" + m);
    let query = supabase.from("tasks").select("id,text,done,due_date,project_id,personal").ilike("text", `%${safe}%`).order("due_date", { ascending: false }).limit(30);
    if (accessLevel!=="full") query = query.or("personal.eq.false,personal.is.null");
    const { data } = await query;
    const { data: projs } = await supabase.from("projects").select("id,title");
    const projMap = {}; (projs||[]).forEach(p => projMap[p.id] = p.title);
    setSearchResults((data||[]).map(t => ({ ...t, filmTitle: projMap[t.project_id] || "ETC." })));
  }

  async function loadIgPosts(){
    const weekStartStr=toDateStr(weekStart);
    const weekEndStr=toDateStr(addDays(weekStart,6));
    const {data}=await supabase.from("social_posts").select("id,caption,date,project,status").gte("date",weekStartStr).lte("date",weekEndStr);
    setIgPosts((data||[]).map(p=>({...p,done:p.status==="Yayınlandı"})));
  }

  const prevWeek=()=>setWeekStart(d=>addDays(d,-7));
  const nextWeek=()=>setWeekStart(d=>addDays(d,7));
  const goToToday=()=>{setWeekStart(getWeekStart(now));setActiveDay(todayIdx);};

  const activeDayDate=addDays(weekStart,activeDay);
  const activeDateStr=toDateStr(activeDayDate);
  const weekStartStr=toDateStr(weekStart);
  const weekEndStr=toDateStr(addDays(weekStart,6));
  const dayTasks=tasks.filter(t=>t.due_date===activeDateStr&&t.due_date>=weekStartStr).sort((a,b)=>a.done!==b.done?(a.done?1:-1):0);
  const dayIgPosts=igPosts.filter(p=>p.date===activeDateStr);
  const dayEvents=gcalEvents.filter(e=>e.date===activeDateStr);
  const selectorProjects=[...projects].filter(p=>p.phase!=="Değerlendirilecek"&&!hiddenProjectIds.includes(p.id)).sort((a,b)=>a.title.localeCompare(b.title,"tr"));

  const adaWeekTasks = tasks.filter(t=>t.due_date>=weekStartStr&&t.due_date<=weekEndStr&&(t.assignee==="ADA"||t.assignee2==="ADA"));
  const adaWeekMinutes = adaWeekTasks.reduce((sum,t)=>sum+(parseInt(hours[t.id])||0),0);
  const adaWeekPercent = Math.round((adaWeekMinutes/WEEKLY_TARGET_MIN)*100);
  const cevikWeekTasks = tasks.filter(t=>t.due_date>=weekStartStr&&t.due_date<=weekEndStr&&(!t.assignee||t.assignee==="CEVIK"||t.assignee2==="CEVIK"));
  const cevikWeekMinutes = cevikWeekTasks.reduce((sum,t)=>sum+(parseInt(hours[t.id])||0),0);
  const cevikWeekPercent = Math.round((cevikWeekMinutes/WEEKLY_TARGET_MIN)*100);

  function saveNote(assignee, note) {
    setNotes(prev => ({ ...prev, [assignee]: note }));
    clearTimeout(noteTimers.current[assignee]);
    noteTimers.current[assignee] = setTimeout(async () => {
      await supabase.from("assignee_notes").upsert({ assignee, note }, { onConflict: "assignee" });
    }, 600);
  }

  async function addTask(text,film,dateStr,assignee,assignee2="",block){
    if(!text.trim()) return;
    const isPersonal=film==="Personal";
    const isEtc=film==="ETC."||isPersonal;
    const proj=projects.find(p=>p.title===film);
    const t={id:uid(),text:text.trim(),done:false,due_date:dateStr,project_id:proj?.id||null,etc:isEtc,personal:isPersonal,assignee,assignee2,notified:true,block:block||null};
    setTasks(prev=>[...prev,{...t,filmTitle:film,filmKind:proj?.kind||"",assignee}]);
    await supabase.from("tasks").insert(t);
  }

  async function handleAddTask(){
    if(!newText.trim()||!addingFor) return;
    const isAda = addingFor==="ADA";
    const assignee = isAda ? "ADA" : "CEVIK";
    const block = addingFor==="CEVIK_2" ? 2 : addingFor==="CEVIK_1" ? 1 : undefined;
    const secondary = newShared ? (isAda?"CEVIK":"ADA") : "";
    await addTask(newText,newFilm,activeDateStr,assignee,secondary,block);
    setNewText(""); setNewShared(false); setAddingFor(null); setNewFilm("ETC.");
  }

  async function toggleBlock(id,currentBlock){
    const next = currentBlock===2 ? 1 : 2;
    setTasks(prev=>prev.map(t=>t.id===id?{...t,block:next}:t));
    await supabase.from("tasks").update({block:next}).eq("id",id);
  }

  async function assignTask(taskId,assignee){
    setTasks(prev=>prev.map(t=>t.id===taskId?{...t,assignee}:t));
    await supabase.from("tasks").update({assignee}).eq("id",taskId);
  }

  async function toggleTask(id,currentDone){
    setTasks(prev=>prev.map(t=>t.id===id?{...t,done:!currentDone}:t));
    await supabase.from("tasks").update({done:!currentDone}).eq("id",id);
  }

  async function postponeTask(id,currentDate){
    const nextStr=toDateStr(addDays(new Date(currentDate),1));
    setTasks(prev=>prev.map(t=>t.id===id?{...t,due_date:nextStr}:t));
    await supabase.from("tasks").update({due_date:nextStr}).eq("id",id);
  }

  async function moveToToday(id){
    setTasks(prev=>prev.map(t=>t.id===id?{...t,due_date:todayStr}:t));
    await supabase.from("tasks").update({due_date:todayStr}).eq("id",id);
  }

  async function renameTask(id,newText){
    if(!newText.trim()) return;
    setTasks(prev=>prev.map(t=>t.id===id?{...t,text:newText.trim()}:t));
    await supabase.from("tasks").update({text:newText.trim()}).eq("id",id);
  }

  async function reassignTask(id, filmTitle) {
    const proj = projects.find(p=>p.title===filmTitle);
    const isPersonal = filmTitle==="Personal";
    const isEtc = filmTitle==="ETC."||isPersonal;
    setTasks(prev=>prev.map(t=>t.id===id?{...t,filmTitle,filmKind:proj?.kind||"",project_id:proj?.id||null,personal:isPersonal}:t));
    await supabase.from("tasks").update({project_id:proj?.id||null,etc:isEtc,personal:isPersonal}).eq("id",id);
  }

  async function removeTask(id){
    setTasks(prev=>prev.filter(t=>t.id!==id));
    await supabase.from("tasks").delete().eq("id",id);
  }

  async function toggleIgPost(id, currentDone) {
    const newStatus = currentDone ? "Taslak" : "Yayınlandı";
    setIgPosts(prev=>prev.map(p=>p.id===id?{...p,status:newStatus,done:!currentDone}:p));
    await supabase.from("social_posts").update({status:newStatus}).eq("id",id);
  }

  const weekLabel=(()=>{
    const s=weekStart,e=addDays(weekStart,6);
    return s.getMonth()===e.getMonth()
      ?`${s.getDate()}–${e.getDate()} ${MONTHS_TR[s.getMonth()]}`
      :`${s.getDate()} ${MONTHS_TR[s.getMonth()]} – ${e.getDate()} ${MONTHS_TR[e.getMonth()]}`;
  })();

  if(loading) return <div style={{flex:1,display:"flex",alignItems:"center",justifyContent:"center",background:WHITE}}><span style={{...mono,color:T.mute}}>Loading</span></div>;

  const cevikTasks = dayTasks.filter(t=>!t.assignee||t.assignee==="CEVIK").sort((a,b)=>taskSortTier(a)-taskSortTier(b));
  const cevikBlock1 = cevikTasks.filter(t=>!t.block||t.block===1);
  const cevikBlock2 = cevikTasks.filter(t=>t.block===2);
  const adaTasks = dayTasks.filter(t=>t.assignee==="ADA").sort((a,b)=>taskSortTier(a)-taskSortTier(b));

  return (
    <div style={{flex:1,overflowY:"auto",background:WHITE,padding:"clamp(24px,5vw,48px)"}}>
      <style>{`
        .home-white-wrap{max-width:1320px;margin:0 auto;display:grid;grid-template-columns:1fr 300px;gap:8px 64px;align-items:start;}
        .home-main{min-width:0;}
        .task-cols{display:grid;grid-template-columns:1fr 1fr;gap:0;}
        .task-cols>div{padding:0 40px;}
        .task-cols>div:first-child{padding-left:0;border-right:2px solid ${T.ink};}
        .task-cols>div:last-child{padding-right:0;}
        @media(max-width:1080px){.home-white-wrap{grid-template-columns:1fr!important;}.home-rail{margin-top:32px;padding-top:24px;border-top:0.5px solid ${T.borderLight};}}
        @media(max-width:600px){.task-cols{grid-template-columns:1fr!important;}.task-cols>div{padding:0!important;border-right:none!important;}.task-cols>div:not(:first-child){border-top:2px solid ${T.ink};padding-top:20px!important;margin-top:20px;}}
      `}</style>

      <div className="home-white-wrap">
      <div style={{gridColumn:"1/-1"}}>

        {/* Header */}
        <div style={{display:"flex",alignItems:"baseline",gap:8,marginBottom:10}}>
          <span onClick={prevWeek} style={{...mono,fontSize:12,color:T.border,cursor:"pointer"}}>‹</span>
          <span style={{fontSize:20,fontWeight:500,color:T.ink}}>{DAYS_TR[activeDay]}</span>
          <span onClick={nextWeek} style={{...mono,fontSize:12,color:T.border,cursor:"pointer"}}>›</span>
          <span onClick={goToToday} style={{...mono,fontSize:9,color:T.mute,cursor:"pointer",border:`1px solid ${T.border}`,borderRadius:4,padding:"3px 8px",marginLeft:6}}>Bugün</span>
        </div>
        <div style={{display:"flex",alignItems:"flex-end",gap:18,marginBottom:8}}>
          {DAYS_SHORT.map((d,i)=>{
            const dd=addDays(weekStart,i);
            const isToday=toDateStr(dd)===todayStr;
            const isActive=activeDay===i;
            return (
              <span key={i} onClick={()=>setActiveDay(i)}
                style={{display:"flex",flexDirection:"column",alignItems:"center",gap:3,cursor:"pointer"}}>
                <span style={{...mono,fontSize:9,color:(isActive||isToday)?T.ink:T.border}}>{dd.getDate()}</span>
                <span style={{...mono,fontSize:11,color:(isActive||isToday)?T.ink:T.mute,fontWeight:isActive?600:400,borderBottom:isToday?`2px solid ${T.ink}`:"2px solid transparent",paddingBottom:2}}>
                  {d}
                </span>
              </span>
            );
          })}
        </div>
        <div style={{...mono,fontSize:10,color:T.border,marginBottom:28}}>{weekLabel}</div>

        {/* Search + catchup — minimal icon triggers */}
        <div style={{display:"flex",gap:14,marginBottom:20}}>
          <span onClick={()=>{setShowSearch(s=>!s);setShowCatchup(false);setSearchQuery("");setSearchResults([]);}}
            style={{...mono,fontSize:10,color:showSearch?T.ink:T.mute,cursor:"pointer"}}>ara</span>
          <span onClick={()=>{setShowCatchup(s=>!s);setShowSearch(false);}}
            style={{...mono,fontSize:10,color:showCatchup?T.ink:T.mute,cursor:"pointer"}}>bekleyenler</span>
        </div>

        {showSearch&&(
          <div style={{marginBottom:24,paddingBottom:16,borderBottom:`0.5px solid ${T.borderLight}`}}>
            <input value={searchQuery} onChange={e=>{setSearchQuery(e.target.value);searchTasks(e.target.value);}}
              placeholder="Görev ara…" autoFocus
              style={{width:"100%",border:"none",borderBottom:`1px solid ${T.border}`,fontSize:14,fontFamily:"'Inter',sans-serif",color:T.ink,outline:"none",padding:"4px 0",marginBottom:10,background:"transparent"}}/>
            {searchResults.map(t=>(
              <div key={t.id} style={{display:"flex",alignItems:"center",gap:8,padding:"6px 0",fontSize:13}}>
                <span style={{flex:1,color:t.done?T.mute:T.ink,textDecoration:t.done?"line-through":"none"}}>{t.text}</span>
                <span style={{...mono,fontSize:9,color:T.mute}}>{t.filmTitle}</span>
                <span style={{...mono,fontSize:9,color:T.border}}>{t.due_date}</span>
              </div>
            ))}
          </div>
        )}

        {showCatchup&&(()=>{
          const unfinished=tasks.filter(t=>!t.done&&t.due_date&&t.due_date<todayStr&&(accessLevel==="full"||!t.personal)).sort((a,b)=>a.due_date.localeCompare(b.due_date));
          return (
            <div style={{marginBottom:24,paddingBottom:16,borderBottom:`0.5px solid ${T.borderLight}`}}>
              {unfinished.length===0?(
                <div style={{...mono,fontSize:11,color:T.mute}}>Bekleyen görev yok ✓</div>
              ):unfinished.map(t=>(
                <div key={t.id} style={{display:"flex",alignItems:"center",gap:8,padding:"6px 0",fontSize:13}}>
                  <span style={{...mono,fontSize:9,color:"#c0392b",flexShrink:0}}>{t.due_date.slice(5)}</span>
                  <span style={{flex:1,color:t.personal?"#7ba3d0":T.ink}}>{t.text}</span>
                  <span onClick={()=>moveToToday(t.id)} style={{...mono,fontSize:9,color:T.mute,cursor:"pointer"}}>bugüne taşı</span>
                  <span onClick={()=>removeTask(t.id)} style={{fontSize:13,color:T.mute,cursor:"pointer",lineHeight:1}} title="Sil">×</span>
                </div>
              ))}
            </div>
          );
        })()}

      </div>

      <div className="home-main">
        {/* İki kolon */}
        <div className="task-cols">
          <div>
            <div style={{...mono,fontSize:11,color:CEVIK_COLOR,letterSpacing:"0.06em",fontWeight:600,marginBottom:4}}>
              CEVİK{accessLevel==="full"&&<span style={{color:T.mute,fontWeight:400}}> · %{cevikWeekPercent} · {cevikWeekMinutes}'</span>}
            </div>
            <div style={{...mono,fontSize:9,color:T.border,letterSpacing:"0.06em",marginTop:10,marginBottom:2}}>I.</div>
            {cevikBlock1.map(t=><TaskRow key={t.id} t={t} colAssignee="CEVIK" accessLevel={accessLevel} selectorProjects={selectorProjects} hours={hours} setHour={setHour} toggleTask={toggleTask} renameTask={renameTask} reassignTask={reassignTask} assignTask={assignTask} postponeTask={postponeTask} removeTask={removeTask} onToggleBlock={toggleBlock}/>)}
            <AddRow sectionKey="CEVIK_1" assignee="CEVIK" addingFor={addingFor} setAddingFor={setAddingFor} newText={newText} setNewText={setNewText} handleAddTask={handleAddTask} newFilm={newFilm} setNewFilm={setNewFilm} newShared={newShared} setNewShared={setNewShared} accessLevel={accessLevel} selectorProjects={selectorProjects} addInputRef={addInputRef}/>
            <div style={{...mono,fontSize:9,color:T.border,letterSpacing:"0.06em",marginTop:16,marginBottom:2}}>II.</div>
            {cevikBlock2.map(t=><TaskRow key={t.id} t={t} colAssignee="CEVIK" accessLevel={accessLevel} selectorProjects={selectorProjects} hours={hours} setHour={setHour} toggleTask={toggleTask} renameTask={renameTask} reassignTask={reassignTask} assignTask={assignTask} postponeTask={postponeTask} removeTask={removeTask} onToggleBlock={toggleBlock}/>)}
            <AddRow sectionKey="CEVIK_2" assignee="CEVIK" addingFor={addingFor} setAddingFor={setAddingFor} newText={newText} setNewText={setNewText} handleAddTask={handleAddTask} newFilm={newFilm} setNewFilm={setNewFilm} newShared={newShared} setNewShared={setNewShared} accessLevel={accessLevel} selectorProjects={selectorProjects} addInputRef={addInputRef}/>
            <div style={{marginTop:20,paddingTop:14,borderTop:`0.5px solid ${T.borderLight}`}}>
              <div style={{...mono,fontSize:9,color:T.border,letterSpacing:"0.06em",marginBottom:6}}>NOT</div>
              <textarea value={notes.CEVIK} onChange={e=>saveNote("CEVIK",e.target.value)}
                placeholder="Not…"
                style={{width:"100%",border:"none",background:"transparent",padding:0,fontSize:14,fontFamily:"Georgia,'Times New Roman',serif",fontStyle:"italic",resize:"vertical",minHeight:260,lineHeight:1.65,color:T.ink,outline:"none",boxSizing:"border-box"}}/>
            </div>
          </div>
          <div>
            <div style={{...mono,fontSize:11,color:ADA_COLOR,letterSpacing:"0.06em",fontWeight:600,marginBottom:4}}>
              ADA <span style={{color:T.mute,fontWeight:400}}>· %{adaWeekPercent} · {adaWeekMinutes}'</span>
            </div>
            {adaTasks.map(t=><TaskRow key={t.id} t={t} colAssignee="ADA" accessLevel={accessLevel} selectorProjects={selectorProjects} hours={hours} setHour={setHour} toggleTask={toggleTask} renameTask={renameTask} reassignTask={reassignTask} assignTask={assignTask} postponeTask={postponeTask} removeTask={removeTask}/>)}
            {dayIgPosts.map(post=>(
              <div key={post.id} style={{display:"flex",alignItems:"center",gap:10,padding:"11px 0",borderBottom:`0.5px solid ${T.borderLight}`}}>
                <div onClick={()=>toggleIgPost(post.id,post.done)}
                  style={{width:16,height:16,borderRadius:4,flexShrink:0,cursor:"pointer",background:post.status==="Yayınlandı"?"#e4572e":"transparent",border:"1.5px solid #e4572e",display:"flex",alignItems:"center",justifyContent:"center"}}>
                  {post.status==="Yayınlandı"&&<span style={{color:"#fff",fontSize:8}}>✓</span>}
                </div>
                <span style={{flex:1,fontSize:14,color:post.status==="Yayınlandı"?T.mute:T.ink,textDecoration:post.status==="Yayınlandı"?"line-through":"none"}}>
                  <span style={{color:"#e4572e",fontWeight:500}}>IG: </span>{post.caption}
                </span>
              </div>
            ))}
            <AddRow sectionKey="ADA" assignee="ADA" addingFor={addingFor} setAddingFor={setAddingFor} newText={newText} setNewText={setNewText} handleAddTask={handleAddTask} newFilm={newFilm} setNewFilm={setNewFilm} newShared={newShared} setNewShared={setNewShared} accessLevel={accessLevel} selectorProjects={selectorProjects} addInputRef={addInputRef}/>
            <div style={{marginTop:20,paddingTop:14,borderTop:`0.5px solid ${T.borderLight}`}}>
              <div style={{...mono,fontSize:9,color:T.border,letterSpacing:"0.06em",marginBottom:6}}>NOT</div>
              <textarea value={notes.ADA} onChange={e=>saveNote("ADA",e.target.value)}
                placeholder="Not…"
                style={{width:"100%",border:"none",background:"transparent",padding:0,fontSize:14,fontFamily:"Georgia,'Times New Roman',serif",fontStyle:"italic",resize:"vertical",minHeight:260,lineHeight:1.65,color:T.ink,outline:"none",boxSizing:"border-box"}}/>
            </div>
          </div>
        </div>

      </div>

      {/* Sağ üst — genel not alanı */}
      <div className="home-rail">
        <div style={{...mono,fontSize:9,color:T.border,letterSpacing:"0.06em",marginBottom:10}}>NOT</div>
        <textarea value={notes.GENEL} onChange={e=>saveNote("GENEL",e.target.value)}
          placeholder="Genel not…"
          style={{width:"100%",border:"none",background:"transparent",padding:0,fontSize:14,fontFamily:"Georgia,'Times New Roman',serif",fontStyle:"italic",resize:"vertical",minHeight:400,lineHeight:1.65,color:T.ink,outline:"none",boxSizing:"border-box"}}/>
      </div>

      {/* Takvim — iki sütunun altında, tam genişlikte */}
      <div style={{gridColumn:"1/-1",marginTop:8,paddingTop:20,borderTop:`0.5px solid ${T.borderLight}`}}>
        <div style={{...mono,fontSize:11,color:T.mute,letterSpacing:"0.06em",marginBottom:14}}>TAKVİM</div>
        {dayEvents.length===0?(
          <div style={{...mono,fontSize:11,color:T.border}}>Bu gün için etkinlik yok.</div>
        ):dayEvents.map(ev=>(
          <div key={ev.id} style={{display:"flex",alignItems:"center",gap:10,padding:"9px 0",borderBottom:`0.5px solid ${T.borderLight}`}}>
            <span style={{width:7,height:7,borderRadius:"50%",background:ev.color,flexShrink:0}}/>
            <span style={{flex:1,fontSize:13,color:T.ink}}>{ev.title}</span>
            {ev.time&&<span style={{...mono,fontSize:10,color:T.mute}}>{ev.time}</span>}
          </div>
        ))}
      </div>

      </div>
    </div>
  );
}
